import _ from "lodash";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";
import { PartnersService } from "../domain/services/PartnersService";
import { IPartners, PartnersFlatCollection } from "../domain/models/Partners";
import { PartnersDTO } from "../domain/models/dto/PartnersDTO";
import { PartnersMapper } from "../mappers/PartnersMapper";

export class PartnersServiceImpl
    extends BaseServiceImpl
    implements PartnersService
{
    static _instance: PartnersService;
    private partnersMapper = new PartnersMapper();
    private readonly partnersEndpoint = "/api/v1/partners";

    private constructor() {
        super();
    }

    static getInstance(): PartnersService {
        if (!PartnersServiceImpl._instance) {
            PartnersServiceImpl._instance = new PartnersServiceImpl();
        }
        return PartnersServiceImpl._instance;
    }

    instantiate(entity: IPartners): IPartners {
        return this.partnersMapper.fromDTO(entity);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: any
    ): Promise<CollectionResponse<IPartners>> {
        try {
            let url = `${this.partnersEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (
                    key &&
                    !_.isNil(value) &&
                    (!_.isEmpty(value) || _.isBoolean(value))
                ) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<PartnersFlatCollection>(url);

            return {
                ...result,
                rows: result.rows.map((dto) =>
                    this.partnersMapper.fromDTO(dto)
                ),
            };
        } catch (err) {
            console.log("Error in PartnersServiceImpl.getAll");
            console.log(err);
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: IPartners): Promise<IPartners> {
        try {
            const encodedDto = this.partnersMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(
                    `${this.partnersEndpoint}/${dto.id}`,
                    encodedDto
                );
            }
            return this.post(this.partnersEndpoint, encodedDto);
        } catch (err) {
            console.log("Error in PartnersServiceImpl.createOrUpdate");
            console.log(err);
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.partnersEndpoint}/${id}`);
        } catch (err) {
            console.log("Error in PartnersServiceImpl.deleteById");
            console.log(err);
        }
    }

    async getById(id: string): Promise<IPartners | null> {
        try {
            if (!id) return null;

            const dto = await this.get<PartnersDTO>(
                `${this.partnersEndpoint}/${id}?getEntityTranslations=true`
            );
            return this.partnersMapper.fromDTO(dto);
        } catch (err) {
            console.log("Error in PartnersServiceImpl.getById");
            console.log(err);
            return null;
        }
    }
}

export function partnersServiceFactory(): PartnersService {
    return PartnersServiceImpl.getInstance();
}
